import { useState, useEffect, useCallback } from 'preact/hooks'
import { dynamicsApi } from '@/api/dynamics'
import { apiClient } from '@/api/client'
import type { DynamicsConnectionStatus } from '@/types/dynamics'

export function useDynamicsAuth(clientCompanyId: string, enabled = true) {
    const [status, setStatus] = useState<DynamicsConnectionStatus | null>(null)
    const [isLoading, setIsLoading] = useState(enabled && !!clientCompanyId)
    const [isConnecting, setIsConnecting] = useState(false)
    // The callback page's own words for why the connect did not complete. Kept rather than discarded
    // because the one case worth reading is the Dataverse change-tracking refusal, which tells the
    // customer's admin exactly which setting to switch on (ORB-621).
    const [connectError, setConnectError] = useState<string | null>(null)

    const checkStatus = useCallback(async () => {
        if (!clientCompanyId) {
            setIsLoading(false)
            return
        }
        setIsLoading(true)
        try {
            const response = await dynamicsApi.getStatus(clientCompanyId)
            setStatus(response.data ?? { isConnected: false })
        } catch {
            setStatus({ isConnected: false })
        } finally {
            setIsLoading(false)
        }
    }, [clientCompanyId])

    useEffect(() => {
        if (enabled) {
            checkStatus()
        } else {
            // Panel closed: drop stale status and arm loading so the next open paints the
            // spinner immediately instead of flashing last session's status. The connect error
            // goes with it — it belongs to the attempt that produced it, and surviving the close
            // would greet the next open with a refusal the user has not earned yet.
            setStatus(null)
            setIsLoading(true)
            setConnectError(null)
        }
    }, [enabled, checkStatus])

    // Initiate OAuth flow in a popup. The Dataverse environment URL is required up front because
    // the Entra resource scope is per-org.
    const connect = useCallback(async (environmentUrl: string) => {
        if (!environmentUrl) return
        setIsConnecting(true)
        setConnectError(null)
        try {
            const response = await dynamicsApi.initiate(environmentUrl)
            const authorizationUrl = response.data?.authorizationUrl
            if (!authorizationUrl) {
                throw new Error('Failed to get authorization URL')
            }

            // InitiateDynamicsOAuth builds this from the configured Entra authorize endpoint, so it
            // is not caller-controlled — but never hand a non-https URL to window.open regardless.
            // Also throws on a malformed URL, which the surrounding catch turns into a no-op.
            if (new URL(authorizationUrl).protocol !== 'https:') {
                throw new Error('Refusing to open a non-https authorization URL')
            }

            const width = 600
            const height = 800
            const left = window.screenX + (window.outerWidth - width) / 2
            const top = window.screenY + (window.outerHeight - height) / 2
            const popup = window.open(
                authorizationUrl,
                'dynamics-oauth',
                `width=${width},height=${height},left=${left},top=${top},popup=yes`
            )

            if (!popup) {
                setIsConnecting(false)
                return
            }

            // Poll for popup close
            const pollTimer = setInterval(() => {
                if (popup.closed) {
                    clearInterval(pollTimer)
                    window.removeEventListener('message', handleMessage)
                    setIsConnecting(false)
                    checkStatus()
                }
            }, 500)

            // Listen for postMessage from the callback page
            const apiOrigin = new URL(apiClient.getBaseUrl()).origin
            const handleMessage = (event: MessageEvent) => {
                if (event.origin !== window.location.origin && event.origin !== apiOrigin) return
                if (event.source !== popup) return
                if (event.data?.type === 'dynamics-oauth-success') {
                    clearInterval(pollTimer)
                    window.removeEventListener('message', handleMessage)
                    popup.close()
                    setIsConnecting(false)
                    checkStatus()
                }
                if (event.data?.type === 'dynamics-oauth-error') {
                    clearInterval(pollTimer)
                    window.removeEventListener('message', handleMessage)
                    popup.close()
                    setIsConnecting(false)
                    // The message is composed by our own callback function, never echoed from the
                    // provider, so it is safe to render as text. Anything else is treated as no message
                    // rather than shown raw.
                    setConnectError(typeof event.data.error === 'string' && event.data.error ? event.data.error : null)
                }
            }
            window.addEventListener('message', handleMessage)
        } catch {
            setIsConnecting(false)
        }
    }, [checkStatus])

    const disconnect = useCallback(async () => {
        try {
            await dynamicsApi.disconnect(clientCompanyId)
            setStatus({ isConnected: false })
        } catch {
            // Disconnect failed — status unchanged
        }
    }, [clientCompanyId])

    return {
        isConnected: status?.isConnected ?? false,
        connectionId: status?.id,
        status: status?.status ?? '',
        scopes: status?.scopes ?? '',
        environmentUrl: status?.environmentUrl ?? null,
        accessTokenExpiresUtc: status?.accessTokenExpiresUtc ?? null,
        // Only an explicit false is actionable — null/undefined means "not established yet", which
        // reads the same as healthy and must not raise a warning (ORB-621).
        isOpportunityChangeTrackingOff: status?.opportunityChangeTrackingEnabled === false,
        // Health parity fields (surfaced for the summary's status badge + error detail).
        isSoftFailure: status?.isSoftFailure ?? false,
        consecutiveFailureCount: status?.consecutiveFailureCount ?? 0,
        recentErrors: status?.recentErrors ?? [],
        aiSummary: status?.aiSummary ?? null,
        // Summary readout fields.
        connectedAtUtc: status?.connectedAtUtc ?? null,
        lastOutboundSyncUtc: status?.lastOutboundSyncUtc ?? null,
        lastInboundSyncUtc: status?.lastInboundSyncUtc ?? null,
        isLoading,
        isConnecting,
        connectError,
        connect,
        disconnect,
        refreshStatus: checkStatus,
    }
}
